/**
 * Service Requests — sales raises a request, support team executes it.
 *
 * Lifecycle: requested → accepted → in_progress → completed
 *                                             ↘ rejected / cancelled
 *
 * Sales side: create request, see status updates, cancel while still
 *             "requested".
 * Support side: queue of incoming, assign-self / accept / start / complete
 *               with work notes.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFiscalYear } from '../contexts/FiscalYearContext';
import api from '../utils/api';
import { hasPermission } from '../utils/permissions';
import { toast } from 'sonner';

const REQUEST_TYPES = [
  'Installation', 'Demo', 'Site Survey', 'Troubleshooting',
  'Maintenance', 'Training', 'Delivery', 'Other',
];
const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700' },
  { value: 'normal', label: 'Normal', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-amber-100 text-amber-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' },
];
const STATUS_META = {
  requested:   { label: 'Requested',   color: 'bg-slate-200 text-slate-800' },
  accepted:    { label: 'Accepted',    color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  completed:   { label: 'Completed',   color: 'bg-emerald-100 text-emerald-700' },
  rejected:    { label: 'Rejected',    color: 'bg-red-100 text-red-700' },
  cancelled:   { label: 'Cancelled',   color: 'bg-gray-100 text-gray-600' },
};

const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const ServiceRequests = () => {
  const { user } = useAuth();
  const { selectedFy } = useFiscalYear();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSupport = user?.role === 'support';
  const canCreate = hasPermission(user, 'service_requests', 'create');

  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [supportUsers, setSupportUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', priority: '', search: '' });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [sr, cust, users] = await Promise.all([
        api.get('/service-requests'),
        api.get('/customers'),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      setList(sr.data || []);
      setCustomers(cust.data || []);
      setSupportUsers(
        (users.data || []).filter((u) =>
          ['support', 'admin', 'super_admin'].includes(u.role)
        )
      );
    } catch (e) {
      console.error(e);
      toast.error('Failed to load service requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = list.filter((r) => {
    if (filter.status && r.status !== filter.status) return false;
    if (filter.priority && r.priority !== filter.priority) return false;
    // Active Fiscal Year scope — filter on requested_date when set.
    if (selectedFy) {
      const day = (r.requested_date || r.created_at || '').slice(0, 10);
      if (!day) return false;
      if (day < selectedFy.start_date) return false;
      if (day > selectedFy.end_date) return false;
    }
    if (filter.search) {
      const q = filter.search.toLowerCase();
      if (
        !(r.request_number || '').toLowerCase().includes(q) &&
        !(r.title || '').toLowerCase().includes(q) &&
        !(r.customer_name || '').toLowerCase().includes(q) &&
        !(r.request_type || '').toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  // Bucket counts for header chips
  const openCount = list.filter((r) =>
    ['requested', 'accepted', 'in_progress'].includes(r.status)
  ).length;
  const myOpenCount = list.filter(
    (r) =>
      ['requested', 'accepted', 'in_progress'].includes(r.status) &&
      (r.requested_by === user?.id || r.assigned_to === user?.id)
  ).length;

  const openCreate = () => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setShowForm(true);
  };

  const updateStatus = async (r, nextStatus, extra = {}) => {
    try {
      await api.put(`/service-requests/${r.id}`, { status: nextStatus, ...extra });
      toast.success(`Status updated to ${STATUS_META[nextStatus]?.label || nextStatus}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const assignToMe = async (r) => {
    try {
      await api.put(`/service-requests/${r.id}`, { assigned_to: user.id });
      toast.success('Assigned to you');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete service request ${r.request_number}?`)) return;
    try {
      await api.delete(`/service-requests/${r.id}`);
      toast.success('Deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="service-requests-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">🛎️ Service Requests</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isSupport
              ? 'Incoming work from sales — assign, accept, complete.'
              : isAdmin
              ? 'All raised requests across the company.'
              : 'Raise a request and the support team will pick it up.'}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <span className="text-xs px-3 py-1.5 rounded-full bg-slate-100 text-slate-700" data-testid="sr-open-count">
            Open: <b>{openCount}</b>
          </span>
          <span className="text-xs px-3 py-1.5 rounded-full bg-blue-100 text-blue-700" data-testid="sr-my-open-count">
            My open: <b>{myOpenCount}</b>
          </span>
          {canCreate && (
            <button
              onClick={openCreate}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow"
              data-testid="sr-new-btn"
            >
              + New Request
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <input
          type="text"
          placeholder="Search number / title / customer…"
          value={filter.search}
          onChange={(e) => setFilter({ ...filter, search: e.target.value })}
          className="border rounded-lg px-3 py-2 text-sm"
          data-testid="sr-search-input"
        />
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="border rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select
          value={filter.priority}
          onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
          className="border rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">All priorities</option>
          {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-12 text-center text-gray-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-gray-400 bg-gray-50 rounded-lg border-2 border-dashed">
          No service requests found.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <ServiceRequestCard
              key={r.id}
              r={r}
              user={user}
              isAdmin={isAdmin}
              isSupport={isSupport}
              onEdit={openEdit}
              onAssignToMe={assignToMe}
              onUpdateStatus={updateStatus}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {showForm && (
        <ServiceRequestForm
          existing={editing}
          customers={customers}
          supportUsers={supportUsers}
          currentUser={user}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchAll(); }}
        />
      )}
    </div>
  );
};

// ── Individual request card ────────────────────────────────────────────────
const ServiceRequestCard = ({ r, user, isAdmin, isSupport, onEdit, onAssignToMe, onUpdateStatus, onDelete }) => {
  const status = STATUS_META[r.status] || STATUS_META.requested;
  const prio = PRIORITIES.find((p) => p.value === r.priority) || PRIORITIES[1];
  const isMine = r.requested_by === user?.id;
  const isAssigned = r.assigned_to === user?.id;
  const canEdit = isAdmin || isMine || isAssigned;
  const canDelete = isAdmin || (isMine && r.status === 'requested');

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition p-4 border" data-testid={`sr-card-${r.request_number}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-mono text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">{r.request_number}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${status.color}`}>{status.label}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${prio.color}`}>{prio.label}</span>
            <span className="text-xs text-gray-500">{r.request_type}</span>
          </div>
          <div className="text-base font-semibold text-gray-900 truncate">{r.title}</div>
          <div className="text-sm text-gray-600 mt-1">
            <span className="font-medium">{r.customer_name}</span>
            {r.location && <span className="text-gray-400"> · {r.location}</span>}
          </div>
          {r.description && (
            <div className="text-sm text-gray-500 mt-1 line-clamp-2">{r.description}</div>
          )}
          <div className="text-xs text-gray-400 mt-2 flex flex-wrap gap-x-3 gap-y-1">
            <span>Requested by <b className="text-gray-600">{r.requested_by_name || '—'}</b></span>
            <span>· Wants by {fmtDate(r.requested_date)}</span>
            {r.assigned_to_name && <span>· Assigned to <b className="text-gray-600">{r.assigned_to_name}</b></span>}
            <span>· Created {fmtDate(r.created_at)}</span>
            {r.linked_activity_id && (
              <a
                href="/activities"
                className="text-emerald-700 hover:text-emerald-900 font-semibold underline"
                data-testid={`sr-${r.request_number}-activity-link`}
              >
                · 📋 Activity created — open Activities
              </a>
            )}
          </div>
          {r.status === 'completed' && (r.work_done || r.hours_spent) && (
            <div className="mt-2 text-xs bg-emerald-50 border border-emerald-100 rounded p-2 text-emerald-800">
              <b>Completed:</b> {r.work_done || '—'}
              {r.hours_spent ? <span> · {r.hours_spent}h spent</span> : null}
              {r.parts_used ? <span> · Parts: {r.parts_used}</span> : null}
            </div>
          )}
          {(r.status === 'rejected' || r.status === 'cancelled') && r.resolution_reason && (
            <div className="mt-2 text-xs bg-red-50 border border-red-100 rounded p-2 text-red-700">
              <b>Reason:</b> {r.resolution_reason}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          {/* Support actions — only Accept / Reject. Once accepted, the
              ticket spawns an Activity and the actual work is tracked there. */}
          {(isSupport || isAdmin) && r.status === 'requested' && !r.assigned_to && (
            <button onClick={() => onAssignToMe(r)} className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 px-3 py-1.5 rounded">Assign to me</button>
          )}
          {(isSupport || isAdmin) && r.status === 'requested' && (
            <button onClick={() => onUpdateStatus(r, 'accepted')} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded" data-testid={`sr-${r.request_number}-accept-btn`}>Accept</button>
          )}
          {(isSupport || isAdmin) && r.status === 'requested' && (
            <button
              onClick={() => {
                const reason = window.prompt('Reject reason:');
                if (reason) onUpdateStatus(r, 'rejected', { resolution_reason: reason });
              }}
              className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded"
              data-testid={`sr-${r.request_number}-reject-btn`}
            >
              Reject
            </button>
          )}
          {/* Sales/owner actions */}
          {canEdit && r.status === 'requested' && (
            <button onClick={() => onEdit(r)} className="text-xs bg-white hover:bg-gray-100 text-gray-700 border px-3 py-1.5 rounded">Edit</button>
          )}
          {isMine && r.status === 'requested' && (
            <button
              onClick={() => {
                const reason = window.prompt('Cancel reason (optional):') || '';
                onUpdateStatus(r, 'cancelled', { resolution_reason: reason });
              }}
              className="text-xs bg-gray-50 hover:bg-gray-100 text-gray-700 border px-3 py-1.5 rounded"
            >
              Cancel
            </button>
          )}
          {canDelete && (
            <button onClick={() => onDelete(r)} className="text-xs text-red-600 hover:text-red-800 px-2">🗑</button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Create / Edit / Complete modal ─────────────────────────────────────────
const ServiceRequestForm = ({ existing, customers, supportUsers, currentUser, onClose, onSaved }) => {
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  const isSupport = currentUser?.role === 'support';
  const isEdit = !!existing;
  const isCompleting = existing && (existing.status === 'accepted' || existing.status === 'in_progress');

  const [form, setForm] = useState(() => existing || {
    customer_id: '',
    customer_name: '',
    request_type: 'Installation',
    title: '',
    description: '',
    priority: 'normal',
    requested_date: '',
    location: '',
    assigned_to: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm({ ...form, customer_id: id, customer_name: c?.name || '' });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.customer_id || !form.title) {
      toast.error('Customer and Title are required');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        customer_id: form.customer_id,
        customer_name: form.customer_name,
        request_type: form.request_type,
        title: form.title,
        description: form.description || '',
        priority: form.priority || 'normal',
        requested_date: form.requested_date || null,
        location: form.location || '',
        assigned_to: form.assigned_to || null,
        notes: form.notes || '',
      };
      if (isEdit) {
        // Completing — include completion fields and set status to completed
        if (isCompleting) {
          payload.status = 'completed';
          payload.work_done = form.work_done || '';
          payload.hours_spent = form.hours_spent ? parseFloat(form.hours_spent) : null;
          payload.parts_used = form.parts_used || '';
        }
        await api.put(`/service-requests/${existing.id}`, payload);
        toast.success(isCompleting ? 'Marked as completed' : 'Updated');
      } else {
        await api.post('/service-requests', payload);
        toast.success('Service request raised — support has been notified.');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-8" data-testid="sr-form-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-bold text-gray-900">
            {isCompleting ? '✅ Complete Service Request'
              : isEdit ? '✏️ Edit Service Request'
              : '➕ New Service Request'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Customer + Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Customer *</label>
              <select
                required
                value={form.customer_id}
                onChange={(e) => handleCustomer(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                data-testid="sr-customer-select"
              >
                <option value="">Select customer</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Request Type *</label>
              <select
                value={form.request_type}
                onChange={(e) => setForm({ ...form, request_type: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
              >
                {REQUEST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="Short summary (e.g. AMC visit for AC unit at HQ)"
              data-testid="sr-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="Full details for the support team"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
              >
                {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Requested Date</label>
              <input
                type="date"
                value={form.requested_date || ''}
                onChange={(e) => setForm({ ...form, requested_date: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Location</label>
              <input
                type="text"
                value={form.location || ''}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="Site address (optional)"
              />
            </div>
          </div>

          {/* Assign to support (admin/support only) */}
          {(isAdmin || isSupport) && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Assign To (support)</label>
              <select
                value={form.assigned_to || ''}
                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="">— Unassigned —</option>
                {supportUsers.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </div>
          )}

          {/* Completion fields */}
          {isCompleting && (
            <div className="mt-2 p-4 rounded-lg bg-emerald-50 border border-emerald-100 space-y-3">
              <div className="text-sm font-semibold text-emerald-800">Completion details</div>
              <textarea
                rows={2}
                placeholder="What was done"
                value={form.work_done || ''}
                onChange={(e) => setForm({ ...form, work_done: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  step="0.25"
                  placeholder="Hours spent"
                  value={form.hours_spent || ''}
                  onChange={(e) => setForm({ ...form, hours_spent: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  placeholder="Parts used (optional)"
                  value={form.parts_used || ''}
                  onChange={(e) => setForm({ ...form, parts_used: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50">Cancel</button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-semibold disabled:opacity-50"
              data-testid="sr-save-btn"
            >
              {submitting ? 'Saving…' : isCompleting ? 'Mark Completed' : isEdit ? 'Save Changes' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceRequests;
